<template>
    <div class="gap-sm flex flex-col capitalize" :class="{ 'gap-xs': compact }">
        <div v-for="line in lines" :key="line.sla_type.name">
            <div
                v-if="line.sla_tags.length"
                class="p-xs w-full rounded-sm border border-(--border-subtle)"
                :class="{ 'rounded-md': compact }"
            >
                <DropdownContent default-open>
                    <template #trigger>
                        <div class="gap-sm flex h-8 w-full items-center justify-between capitalize">
                            <p class="text-md font-default font-normal">
                                {{ line.sla_type.name }}
                            </p>
                            <div class="items center gap-xs flex">
                                <TagComponent
                                    v-for="tag in line.sla_tags.filter((tag) => tag.value !== '0')"
                                    :key="tag.sla_tag_id"
                                    :value="tag.value === '100' && ['Hours', 'Days'].includes(line.sla_type.unit) ? 'Best effort' : tag.value + appendUnit(line.sla_type.unit)"
                                    class-name="font-default text-sm font-bold text-(--state-info-fg)"
                                />
                            </div>
                        </div>
                    </template>

                    <template #content>
                        <div class="mt-md gap-sm flex flex-col">
                            <template v-if="!readonly">
                                <KeepAlive>
                                    <RangeInput
                                        v-for="tag in line.sla_tags"
                                        :key="tag.name"
                                        :disabled="readonly"
                                        :name="tag.name"
                                        :unit="unitForRange(line.sla_type.unit)"
                                        :value="tag.value"
                                        @change="
                                            (event) => updateLine(event, line.sla_type.sla_type_id, tag.sla_tag_id)
                                        "
                                    />
                                </KeepAlive>
                            </template>

                            <div
                                v-for="tag in line.sla_tags"
                                v-else
                                :key="tag.name"
                                class="p-xs rounded-sm border border-(--border-subtle)"
                            >
                                <div class="flex w-full items-center justify-between">
                                    <p class="text-md font-default font-normal">{{ tag.name }}</p>
                                    <TagComponent
                                        :value="['100', '0', '0-0'].includes(tag.value) ? { 100: 'Best effort', 0: 'Not applicable', '0-0': 'Not applicable' }[tag.value]! : tag.value + appendUnit(line.sla_type.unit)"
                                        :variant="['0-0', '0'].includes(tag.value) ? 'silver' : 'default'"
                                        class-name="font-default text-sm font-bold text-(--state-info-fg)"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                </DropdownContent>
            </div>
            <RangeInput
                v-else-if="!readonly"
                :disabled="readonly"
                :name="line.sla_type.name"
                :unit="unitForRange(line.sla_type.unit)"
                :value="line.value"
                @change="(event) => updateLine(event, line.sla_type.sla_type_id, undefined)"
            />

            <div v-else class="p-xs rounded-sm border border-(--border-subtle)">
                <div class="flex w-full items-center justify-between">
                    <p class="text-md font-default font-normal">
                        {{ line.sla_type.name }}
                    </p>
                    <TagComponent
                        :value="['100', '0', '0-0'].includes(line.value) ? { 100: 'Best effort', 0: 'Not applicable', '0-0': 'Not applicable' }[line.value]! : line.value + appendUnit(line.sla_type.unit)"
                        :variant="['0-0', '0'].includes(line.value) ? 'silver' : 'default'"
                        class-name="font-default text-sm font-bold text-(--state-info-fg)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { AdminContract } from '@/stores/store-interfaces';
import { onMounted, ref, watch, type PropType } from 'vue';
import { appendUnit } from '@/stores/helpers';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import RangeInput from './range-input.vue';
import DropdownContent from '../misc/dropdown-content.vue';

type RangeUnit = 'C' | '%' | 'Hours';

const props = defineProps({
    // if contains values, should have prop readonly come with
    slaLines: { type: Array as PropType<AdminContract['sla']>, required: true },
    readonly: { type: Boolean, required: false, default: false },
    compact: { type: Boolean, required: false, default: false },
});

const emit = defineEmits(['update']);

const lines = ref<AdminContract['sla']>([]);

const values = ref<{ sla_type_id: string; sla_tag_id?: string; value: string }[]>([]);
const unitForRange = (unit: AdminContract['sla'][number]['sla_type']['unit']): RangeUnit => unit as RangeUnit;

const updateLine = (value: string, typeId: string, tagId: string | undefined) => {
    const valueIndex = values.value.findIndex((sla) => sla.sla_type_id === typeId && sla.sla_tag_id === tagId);

    if (valueIndex === -1) {
        if (tagId === undefined) {
            values.value.push({
                sla_type_id: typeId,
                value,
            });
        } else {
            values.value.push({
                sla_type_id: typeId,
                sla_tag_id: tagId,
                value,
            });
        }
    } else {
        values.value[valueIndex].value = value;
    }

    const line = lines.value.find((line) => line.sla_type.sla_type_id === typeId);
    if (!line) return;

    if (tagId) {
        line.sla_tags.find((tag) => tag.sla_tag_id === tagId)!.value = value;
    } else {
        line.value = value;
    }

    emit('update', values.value);
};

onMounted(() => {
    lines.value = props.slaLines;
    lines.value.forEach((line) => {
        values.value.push({
            sla_type_id: line.sla_type.sla_type_id,
            value: line.value,
        });
        line.sla_tags.forEach((tag) => {
            values.value.push({
                sla_type_id: line.sla_type.sla_type_id,
                sla_tag_id: tag.sla_tag_id,
                value: tag.value,
            });
        });
    });
});

watch(
    () => props.slaLines,
    (value) => {
        lines.value = value;
    },
);
</script>
